/**
 * Builder > Conditions > Add button
 * <ua-builder-conditions-add-button>
 */

// Import light DOM CSS
import './style.scss';

// Import dependencies
const { isEmpty, isDefined, addParametersToURL } = _uo.utility;
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import utilities and components
import { openInNewTab, addUTMParameters } from '@builder/common/utilities';
import { getConditionsGroupById } from '@builder/item/conditions/utilities';
import { canManageConditions } from '@builder/item/conditions/utilities';
import ConditionsGroup from '@builder/item/conditions/conditions-group';
import ItemConditionsModalList from '@builder/item/conditions/modal-list';

// Import style for web component
const styles = require( './web-component-style.scss' );

// eslint-disable-next-line require-jsdoc
@customElement( 'ua-builder-conditions-add-button' )
export class ConditionAddButton extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Properties
	 */

	// optional. If this is defined, the item will be added to the conditions block
	@property( { type: String, attribute: 'action-id' } ) actionID; 
	// optional. If this is defined, the condition will be added to that conditions group instead of creating another
	@property( { type: String, attribute: 'condition-group-id' } ) conditionsGroupID;

	// optional. The size of the button
	@property( { type: String } ) size; 
	@property( { type: Boolean, reactive: true } ) isActionOpen = false;

	// eslint-disable-next-line require-jsdoc, no-useless-constructor
	constructor() {
		super();
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		if ( ! canManageConditions() ) {
			return this.renderDisabledButton();
		}

		return this.renderEnabledButton();
	}

	// eslint-disable-next-line require-jsdoc
	connectedCallback() {
		super.connectedCallback();

		// Disable the button when the item's options is opened
		this.disableWhenUserEditsAction();
	}

	// eslint-disable-next-line require-jsdoc
	disconnectedCallback() {
		super.disconnectedCallback();

		// Remove the event listeners
		document.removeEventListener( 'automator/item/options/open', this.callbackOptionsOpen );
		document.removeEventListener( 'automator/item/options/close', this.callbackOptionsClose );
	}

	/**
	 * Renders the button when it's enabled
	 * 
	 * @return {TemplateResult} The button
	 */
	renderEnabledButton() {
		// Check if this button is on an action, and the action's options are opened
		if ( this.isActionOpen ) {
			return html`
				<uo-tooltip>
					${ UncannyAutomator.i18n.conditions.pleaseSaveActionFirst }

					<uo-button
						color="secondary"
						size=${ ifDefined( this.size ) }
						disabled

						slot="target"
					>
						<slot></slot>
					</uo-button>
				</uo-tooltip>
			`;
		}

		return html`
			<uo-button
				color="secondary"

				size=${ ifDefined( this.size ) }

				@click=${ this.showListOfConditions }
			>
				<slot></slot>
			</uo-button>
		`;
	}

	/**
	 * Renders the button when it's enabled
	 * 
	 * @return {TemplateResult} The button
	 */
	renderDisabledButton() {
		return html`
		
			<div
				@click=${ this.promotePro }
			>
				<uo-tooltip
					max-width="200"
				>
				${ UncannyAutomator.i18n.conditions.unlockConditions }

					<uo-button
						size=${ ifDefined( this.size ) }

						color="secondary"
						disabled

						slot="target"
					>
						<slot></slot>
					</uo-button>
				</uo-tooltip>
			</div>
		`;
	}

	/**
	 * Goes to the next step and renders list of conditions
	 */
	showListOfConditions() {
		// Create the conditions group
		const conditionsGroup = ! isEmpty( this.conditionsGroupID ) 
			? getConditionsGroupById( { conditionsGroupId: this.conditionsGroupID } ) 
			: ( new ConditionsGroup( {} ) );

		// Check if we should add an action automatically
		if ( ! isEmpty( this.actionID ) ) {
			conditionsGroup.actions = [ this.actionID ];
		}

		// Create the modal with the list of conditions
		new ItemConditionsModalList( {
			conditionsGroupId: conditionsGroup.id,
			comingFrom: 'action-filter-button',
		} );
	}

	/**
	 * Disables the button when the user is editing the action
	 *
	 * @return {undefined}
	 */
	disableWhenUserEditsAction() {
		// Check if this button is on an action in the first place
		if ( isEmpty( this.actionID ) ) {
			return;
		}

		// Check if it can manage conditions
		if ( ! canManageConditions() ) {
			return;
		}

		/**
		 * The callback invoked when the options panel is opened
		 * We're following this approach so we can remove the listener when the 
		 * Lit element is removed, as the listener is tied to the document, and not to this node
		 */
		this.callbackOptionsOpen = {
			handleEvent: ( event ) => {
				// Check if it's the action where this button is
				if ( this.actionID != event.detail.itemId ) {
					return;
				}

				// Disable the button
				this.isActionOpen = true;
			}
		};

		// Same as above, but when the panel is closed
		this.callbackOptionsClose = {
			handleEvent: ( event ) => {
				// Check if it's the action where this button is
				if ( this.actionID != event.detail.itemId ) {
					return;
				}

				// Enable the button
				this.isActionOpen = false;
			}
		}

		// Listen when an item is opened
		document.addEventListener( 'automator/item/options/open', this.callbackOptionsOpen );

		// Listen when the item is closed
		document.addEventListener( 'automator/item/options/close', this.callbackOptionsClose );
	}

	/**
	 * Peforms the actions to promote the Pro version
	 * For now, it just sends the user to the pricing page
	 *
	 * @return {undefined}
	 */
	promotePro() {
		// Open URL on a new tab
		openInNewTab(
			addParametersToURL(
				// Add UTM parameters
				addUTMParameters( 'https://automatorplugin.com/pricing/', {
					medium: 'recipe_creator',
					content: 'action_disabled_condition_button',
				} ),
				{
					uap_bpro_context: 'filter'
				}
			)
		);
	}
}
